import React from "react";
import {
  SourceWipsCardProps,
  ReceivingInvoiceLine,
  WipSource,
} from "../../types/traceability";
import { kgFmt, lbsFmt, pcsFmt } from "../../shared/formatting";
import { formatDate } from "../../shared/utilityTsx";

function ReceivingInvoiceBlock({ line }: { line: ReceivingInvoiceLine }) {
  return (
    <div className="border border-gray-200 rounded-lg p-4">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="flex items-center gap-2">
            <h3 className="text-lg font-semibold text-gray-900">
              Receiving Source
            </h3>
            <span className="inline-flex items-center rounded-md bg-blue-50 px-2 py-1 text-xs font-medium text-blue-700 ring-1 ring-inset ring-blue-700/10">
              Supplier
            </span>
          </div>
          {line.receiving_invoice_number && (
            <p className="text-sm text-gray-600 mt-1">
              Invoice: {line.receiving_invoice_number}
            </p>
          )}
          {line.supplier_name && (
            <p className="text-sm text-gray-600">
              Supplier: {line.supplier_name}
            </p>
          )}

          {line.warehouse_name && (
            <p className="text-sm text-gray-600">
              Location: {line.warehouse_location_name} {line.warehouse_name}
            </p>
          )}
        </div>
        <div className="text-right">
          {line.receiving_invoice_id ? (
            <button
              type="button"
              className="text-blue-600 hover:text-blue-800 text-sm font-medium"
              onClick={() =>
                window.open(`/receiving/${line.receiving_invoice_id}`, "_blank")
              }
            >
              View Receiving
            </button>
          ) : null}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {line.receiving_date && (
          <div className="bg-gray-50 rounded-lg p-3">
            <div className="text-sm font-medium text-gray-600 mb-1">
              Receiving Date
            </div>
            <div className="text-xl font-semibold text-gray-900">
              {formatDate(line.receiving_date)}
            </div>
          </div>
        )}
        {line.total_received_kg !== undefined && (
          <div className="bg-gray-50 rounded-lg p-3">
            <div className="text-sm font-medium text-gray-600 mb-1">
              Total Received (kg)
            </div>
            <div className="text-xl font-semibold text-gray-900">
              {kgFmt(line.total_received_kg)}
            </div>
          </div>
        )}
        {line.total_received_lbs !== undefined && (
          <div className="bg-gray-50 rounded-lg p-3">
            <div className="text-sm font-medium text-gray-600 mb-1">
              Total Received (lbs)
            </div>
            <div className="text-xl font-semibold text-gray-900">
              {lbsFmt(line.total_received_lbs)}
            </div>
          </div>
        )}
        {line.total_received_pcs !== undefined && (
          <div className="bg-gray-50 rounded-lg p-3">
            <div className="text-sm font-medium text-gray-600 mb-1">
              Total Received (pcs)
            </div>
            <div className="text-xl font-semibold text-gray-900">
              {pcsFmt(line.total_received_pcs)}
            </div>
          </div>
        )}
        {line.receiving_carton_count !== undefined && (
          <div className="bg-gray-50 rounded-lg p-3">
            <div className="text-sm font-medium text-gray-600 mb-1">
              Carton Count
            </div>
            <div className="text-xl font-semibold text-gray-900">
              {line.receiving_carton_count || 0}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function WipSourceBlock({ source }: { source: WipSource }) {
  return (
    <div className="border border-gray-200 rounded-lg p-4">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">
            WIP {source.wip_lot_number || `#${source.wip_id}`}
          </h3>
          <p className="text-sm text-gray-600">WIP ID: {source.wip_id}</p>
        </div>
        <div className="text-right">
          {source.wip_id ? (
            <button
              type="button"
              className="text-blue-600 hover:text-blue-800 text-sm font-medium"
              onClick={() => window.open(`/wips/${source.wip_id}`, "_blank")}
            >
              View WIP
            </button>
          ) : null}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {source.total_produced_kg !== undefined && (
          <div className="bg-gray-50 rounded-lg p-3">
            <div className="text-sm font-medium text-gray-600 mb-1">
              Total Produced (kg)
            </div>
            <div className="text-xl font-semibold text-gray-900">
              {kgFmt(source.total_produced_kg)}
            </div>
          </div>
        )}
        {source.total_produced_pcs !== undefined && (
          <div className="bg-gray-50 rounded-lg p-3">
            <div className="text-sm font-medium text-gray-600 mb-1">
              Total Produced (pcs)
            </div>
            <div className="text-xl font-semibold text-gray-900">
              {pcsFmt(source.total_produced_pcs)}
            </div>
          </div>
        )}
        {source.carton_count !== undefined && (
          <div className="bg-gray-50 rounded-lg p-3">
            <div className="text-sm font-medium text-gray-600 mb-1">
              Carton Count
            </div>
            <div className="text-xl font-semibold text-gray-900">
              {source.carton_count || 0}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default function SourceWipsCard({
  lotNumber,
  source,
}: SourceWipsCardProps) {
  if (!source) {
    return null;
  }

  const subtitle = "Source WIP or Receiving source for this lot number.";

  return (
    <div className="card-custom bg-white p-6">
      <div className="mb-4">
        <h2 className="text-2xl font-semibold text-gray-900">
          Lot Number: {lotNumber}
        </h2>
        <p className="text-sm text-gray-600 mt-1">{subtitle}</p>
      </div>

      <div className="space-y-4">
        {source.is_wip === true ? (
          <WipSourceBlock source={source} />
        ) : source.receiving_invoices.length === 0 ? (
          <p className="text-sm text-gray-600">
            No receiving invoice rows were returned for this lot.
          </p>
        ) : (
          source.receiving_invoices.map((line) => (
            <ReceivingInvoiceBlock
              key={line.receiving_invoice_id}
              line={line}
            />
          ))
        )}
      </div>
    </div>
  );
}
